import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlineShare,
  HiOutlineDotsVertical,
  HiOutlineEmojiSad,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiX,
  HiCloudUpload,
  HiSearch,
  HiCheckCircle,
  HiExclamationCircle,
  HiClock,
  HiOutlineFilter,
  HiOutlineTrash
} from "react-icons/hi";
import { AnimatePresence, motion } from "framer-motion";
import FileCard from "../Components/FileCard";
import FolderCard from "../Components/FolderCard";
import CreateFolderModal from "../Components/CreateFolderModal";
import ViewFileModal from "../Components/ViewFileModal";
import RenameModal from "../Components/RenameModal";
import ShareModal from "../Components/ShareModal";
import DownloadConfirmModal from "../Components/DownloadConfirmModal";
import DeleteModal from "../Components/DeleteModal";
import {
  getPrivateContentAPI,
  getFolderContentAPI,
  uploadFileAPI,
  renameFileAPI,
  renameFolderAPI,
  grantAccessAPI,
  deleteFolderAPI,
  deleteFileAPI
} from "../services/driveService";
import { toast } from "react-toastify";
import useAuth from "../useAuth";
import { useActivity } from "../ActivityContext";

export default function PrivatePage() {
  const { user } = useAuth();
  const { refreshActivityReport } = useActivity();
  const fileInputRef = useRef(null);

  const [viewSize, setViewSize] = useState(() => {
    const saved = localStorage.getItem("private_viewSize");
    return (saved === "big" ? "small" : saved) || "small";
  });

  const [currentFolderId, setCurrentFolderId] = useState(() => {
    return JSON.parse(localStorage.getItem("private_currentFolderId")) || null;
  });

  const [breadcrumbs, setBreadcrumbs] = useState(() => {
    try {
      const saved = localStorage.getItem("private_breadcrumbs");
      return saved ? JSON.parse(saved) : [{ id: null, name: "Private Drive" }];
    } catch (e) {
      return [{ id: null, name: "Private Drive" }];
    }
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeUploads, setActiveUploads] = useState([]);
  const [isUploadPanelMinimized, setIsUploadPanelMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("private_sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("private_sortOrder") || "asc");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [downloadItem, setDownloadItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    localStorage.setItem("private_currentFolderId", JSON.stringify(currentFolderId));
    localStorage.setItem("private_viewSize", viewSize);
    localStorage.setItem("private_breadcrumbs", JSON.stringify(breadcrumbs));
    localStorage.setItem("private_sortOrder", sortOrder);
    localStorage.setItem("private_sortBy", sortBy);
  }, [currentFolderId, viewSize, breadcrumbs, sortOrder, sortBy]);

  useEffect(() => {
    // Initialize history state on load if empty, so we have a base to 'go back' to
    if (!window.history.state || !window.history.state.breadcrumbs) {
      window.history.replaceState({ breadcrumbs, folderId: currentFolderId }, "");
    }

    const handlePopState = (event) => {
      if (event.state && event.state.breadcrumbs) {
        setCurrentFolderId(event.state.folderId);
        setBreadcrumbs(event.state.breadcrumbs);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentFolderId, sortOrder, sortBy]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      if (currentFolderId === null) {
        console.log("Fetching private content with sort:", sortBy, sortOrder);
        const data = await getPrivateContentAPI(sortBy, sortOrder);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      } else {
        console.log("Fetching folder content with sort:", sortBy, sortOrder);
        const data = await getFolderContentAPI(currentFolderId, sortBy, sortOrder, true);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      }
    } catch (error) {
      toast.error("Failed to load content");
    } finally {
      setLoadingData(false);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = (e.target && e.target.files) ? Array.from(e.target.files) : Array.from(e);
    if (selectedFiles.length === 0) return;

    const newUploads = selectedFiles.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      progress: 0,
      status: 'pending',
      size: file.size,
      abortController: new AbortController()
    }));

    setActiveUploads(prev => [...newUploads, ...prev]);
    setUploading(true);

    for (const uploadItem of newUploads) {
      const file = selectedFiles[newUploads.indexOf(uploadItem)];
      setActiveUploads(prev => prev.map(u => u.id === uploadItem.id ? { ...u, status: 'uploading' } : u));

      try {
        await uploadFileAPI(file, currentFolderId, (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setActiveUploads(prev => prev.map(u => u.id === uploadItem.id ? { ...u, progress: percentCompleted } : u));
        }, uploadItem.abortController.signal, true);

        setActiveUploads(prev => prev.map(u => u.id === uploadItem.id ? { ...u, status: 'success', progress: 100 } : u));
        refreshActivityReport();
      } catch (error) {
        setActiveUploads(prev => prev.map(u => u.id === uploadItem.id ? { ...u, status: error.name === 'CanceledError' ? 'cancelled' : 'error' } : u));
      }
    }
    fetchData();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const totalSelectedSize = useMemo(() => {
    let total = 0;
    selectedItems.forEach(key => {
      const [type, id] = key.split(':');
      if (type === 'folder') {
        const folder = folders.find(f => String(f.id) === String(id));
        if (folder) total += (folder.size || 0);
      } else {
        const file = files.find(f => String(f.id) === String(id));
        if (file) total += (file.size || 0);
      }
    });
    return total;
  }, [selectedItems, folders, files]);

  const handleCancelIndividualUpload = (uploadId) => {
    const upload = activeUploads.find(u => u.id === uploadId);
    if (upload && upload.abortController) {
      upload.abortController.abort();
      setActiveUploads(prev => prev.map(u => u.id === uploadId ? { ...u, status: 'cancelled' } : u));
    }
  };

  const handleToggleSelect = (item, type) => {
    const itemKey = `${type}:${item.id}`;
    setSelectedItems(prev => prev.includes(itemKey) ? prev.filter(k => k !== itemKey) : [...prev, itemKey]);
  };

  const handleSelectAll = () => {
    const allKeys = [...filteredFolders.map(f => `folder:${f.id}`), ...filteredFiles.map(f => `file:${f.id}`)];
    setSelectedItems(selectedItems.length === allKeys.length ? [] : allKeys);
  };

  const isSearching = searchQuery.length > 0 || activeCategory !== "all";

  const filteredFolders = useMemo(() => {
    if (isSearching) return folders.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
    return folders.filter(f => (f.parentId || null) === currentFolderId);
  }, [folders, currentFolderId, isSearching, searchQuery]);

  const filteredFiles = useMemo(() => {
    return files.filter(file => {
      if (!isSearching && (file.folderId || null) !== currentFolderId) return false;
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const extension = (file.extension || file.name.split('.').pop()).toLowerCase();
      const isDoc = ["pdf", "doc", "docx", "txt"].includes(extension);
      const isImg = ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension);
      if (activeCategory === "document") return isDoc && matchesSearch;
      if (activeCategory === "image") return isImg && matchesSearch;
      return matchesSearch;
    });
  }, [searchQuery, activeCategory, files, currentFolderId, isSearching]);

  const handleFolderClick = (folder) => {
    const newBreadcrumbs = [...breadcrumbs, { id: folder.id, name: folder.name }];
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: folder.id }, "");
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolderId(folder.id);
  };

  const handleBreadcrumbClick = (id, index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: id }, "");
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolderId(id);
  };

  const handleBack = () => {
    if (breadcrumbs.length > 1) {
      handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 2].id, breadcrumbs.length - 2);
    }
  };

  const handleForward = () => window.history.forward();

  const handleRenameSubmit = async (newName) => {
    if (!renamingItem) return;
    try {
      renamingItem.type === "file" ? await renameFileAPI(renamingItem.id, newName) : await renameFolderAPI(renamingItem.id, newName);
      toast.success("Item renamed successfully");
      fetchData();
      setRenamingItem(null);
    } catch (error) {
      toast.error(error?.message || "Failed to rename");
    }
  };

  const confirmItemDeletion = async (item, type) => {
    setIsDeleting(true);
    try {
      if (type === "batch") {
        for (const itemKey of selectedItems) {
          const [itemType, itemId] = itemKey.split(":");
          itemType === "folder" ? await deleteFolderAPI(itemId) : await deleteFileAPI(itemId);
        }
        toast.success(`${selectedItems.length} items moved to trash`);
        setSelectedItems([]);
      } else {
        type === "folder" ? await deleteFolderAPI(item.id) : await deleteFileAPI(item.id);
        toast.success(`Item moved to trash`);
      }
      fetchData();
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error?.message || `Failed to delete`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBatchDownload = async () => {
    const selectedFilesCount = selectedItems.filter(k => k.startsWith('file:')).length;
    const selectedFoldersCount = selectedItems.filter(k => k.startsWith('folder:')).length;

    if (selectedFilesCount === 0) {
      if (selectedFoldersCount > 0) {
        toast.info("Only files can be downloaded directly. Folders cannot be downloaded.");
      }
      return;
    }

    if (selectedFoldersCount > 0) {
      toast.warning(`${selectedFoldersCount} folders were skipped. Only files can be downloaded directly.`);
    }

    // Trigger download for all selected files
    selectedItems.forEach(key => {
      const [type, id] = key.split(':');
      if (type === 'file') {
        window.dispatchEvent(new CustomEvent('triggerDownload', { detail: { fileId: id } }));
      }
    });
    setSelectedItems([]);
  };

  const handleBatchShare = () => {
    const itemsToShare = selectedItems.map(key => {
      const [type, id] = key.split(':');
      const item = type === 'folder'
        ? folders.find(f => String(f.id) === String(id))
        : files.find(f => String(f.id) === String(id));
      return { ...item, type };
    }).filter(item => item.id);

    if (itemsToShare.length > 0) {
      setSharingItem(itemsToShare);
    }
  };

  const handleShareSubmit = async (userIds, resources, permission) => {
    try {
      await grantAccessAPI(userIds, resources, permission);
      toast.success(`Access granted successfully`);
      setSharingItem(null);
      setSelectedItems([]);
    } catch (error) {
      toast.error(error?.message || "Failed to grant access");
    }
  };

  return (
    <div className="relative min-h-screen" onDragOver={e => { e.preventDefault(); setIsDragging(true); }} onDragLeave={e => { e.preventDefault(); setIsDragging(false); }} onDrop={e => { e.preventDefault(); setIsDragging(false); handleFileUpload(e.dataTransfer.files); }}>
      <AnimatePresence>
        {isDragging && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[1000] bg-indigo-600/10 backdrop-blur-md flex items-center justify-center p-8 pointer-events-none">
            <div className="bg-white border-4 border-dashed border-indigo-600/30 rounded-[48px] p-12 flex flex-col items-center gap-6 shadow-2xl">
              <div className="w-24 h-24 rounded-3xl bg-indigo-600 text-white flex items-center justify-center"><HiCloudUpload size={48} className="animate-bounce" /></div>
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter text-center">Release to Upload</h2>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" multiple />

      <section className="px-2 pt-2">
        <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-2">Private <span className="text-indigo-600/70 font-medium whitespace-nowrap">Drive</span></h1>
        <p className="text-slate-500 text-[10px] md:text-sm font-bold uppercase tracking-[0.2em]">Your secure personal storage</p>
      </section>

      <section className="py-2">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between bg-white border border-slate-200 rounded-2xl md:rounded-[24px] p-2 gap-3 lg:gap-0 lg:pr-6 shadow-xl overflow-hidden">
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-100 rounded-xl p-1 border border-slate-200 shrink-0">
              <button onClick={handleBack} className="p-2 text-slate-500 hover:text-slate-900 transition-all"><HiOutlineArrowLeft size={18} /></button>
              <button onClick={handleForward} className="p-2 text-slate-500 hover:text-slate-900 transition-all border-l border-slate-200"><HiOutlineArrowRight size={18} /></button>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
              {breadcrumbs.map((b, i) => (
                <button key={b.id || i} onClick={() => handleBreadcrumbClick(b.id, i)} className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-lg ${i === breadcrumbs.length - 1 ? "text-indigo-600 bg-indigo-50" : "text-slate-500"}`}>{b.name}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <AnimatePresence>
              {selectedItems.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center gap-1 bg-indigo-600 p-1 rounded-xl shadow-lg shadow-indigo-100"
                >
                  <div className="px-3 py-1 flex items-center gap-2 border-r border-white/20">
                    <HiCheckCircle className="text-white" size={16} />
                    <div className="flex flex-col leading-tight">
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">{selectedItems.length} SELECTED</span>
                      <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-tighter">{formatFileSize(totalSelectedSize)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleBatchDownload}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Download Selected"
                  >
                    <HiCloudUpload size={18} />
                  </button>
                  <button
                    onClick={handleBatchShare}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Share Selected"
                  >
                    <HiOutlineShare size={18} />
                  </button>
                  <button
                    onClick={() => { setItemToDelete({ type: 'batch' }); setIsDeleteModalOpen(true); }}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Delete Selected"
                  >
                    <HiOutlineTrash size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedItems([])}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all border-l border-white/20"
                    title="Clear Selection"
                  >
                    <HiX size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative group flex-1 lg:w-64">
              <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500" />
              <input type="text" placeholder="Search private items..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-50 border-none rounded-xl pl-11 pr-4 py-2.5 text-xs font-bold text-slate-700 focus:ring-2 focus:ring-indigo-500/20" />
            </div>
            <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/60 shadow-sm backdrop-blur-sm">
              <div className="flex items-center pl-2.5 pr-1 border-r border-slate-200/80 gap-2">
                {loadingData ? (
                  <div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiOutlineFilter size={13} className="text-slate-400" />
                )}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4 relative"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                >
                  <option value="name">Name</option>
                  <option value="date">Date</option>
                  <option value="size">Size</option>
                  <option value="type">Type</option>
                </select>
              </div>
              <div className="px-2">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                >
                  <option value="asc">ASC</option>
                  <option value="desc">DESC</option>
                </select>
              </div>
            </div>
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button onClick={() => setViewSize("small")} className={`p-2 rounded-lg ${viewSize === "small" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}><HiOutlineViewGrid size={14} /></button>
              <button onClick={() => setViewSize("list")} className={`p-2 rounded-lg ${viewSize === "list" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}><HiOutlineViewList size={18} /></button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-500/5 text-blue-600 px-3 md:px-4 py-2.5 rounded-xl flex items-center gap-2 hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-slate-100"
              >
                <span className="text-base md:text-lg">📁</span>
                <span className="text-[9px] md:text-[10px] font-bold tracking-widest whitespace-nowrap uppercase">New Folder</span>
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="bg-indigo-600 text-white text-[10px] uppercase tracking-widest px-5 py-2.5 rounded-xl flex items-center gap-2"><HiCloudUpload size={16} /> Upload</button>
            </div>
          </div>
        </div>
      </section>

      <section className="flex flex-col">
        <div className={`${viewSize === "list" ? "pt-0" : "pt-2"}`}>
          <div key={`${sortOrder}-${sortBy}`} className={`grid ${viewSize === "list" ? "gap-0 grid-cols-1" : "gap-1.5 grid-cols-4 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12"}`}>
            <AnimatePresence>
              {filteredFolders.map((folder, idx) => (
                <FolderCard
                  key={`folder:${folder.id}`}
                  folder={folder}
                  index={idx}
                  view={viewSize === "list" ? "list" : "grid"}
                  isBig={viewSize === "big"}
                  onClick={() => handleFolderClick(folder)}
                  isSelected={selectedItems.includes(`folder:${folder.id}`)}
                  onToggleSelect={() => handleToggleSelect(folder, "folder")}
                  onShare={() => setSharingItem({ ...folder, type: 'folder' })}
                  onRename={() => setRenamingItem({ ...folder, type: 'folder' })}
                  onDelete={() => { setItemToDelete({ ...folder, type: 'folder' }); setIsDeleteModalOpen(true); }}
                />
              ))}
              {filteredFiles.map((file, idx) => (
                <FileCard
                  key={`file:${file.id}`}
                  file={file}
                  index={idx + filteredFolders.length}
                  view={viewSize === "list" ? "list" : "grid"}
                  isBig={viewSize === "big"}
                  onView={() => setViewingFile(file)}
                  isSelected={selectedItems.includes(`file:${file.id}`)}
                  onToggleSelect={() => handleToggleSelect(file, "file")}
                  onDownloadClick={setDownloadItem}
                  onShare={() => setSharingItem({ ...file, type: 'file' })}
                  onRename={() => setRenamingItem({ ...file, type: 'file' })}
                  onDelete={() => { setItemToDelete({ ...file, type: 'file' }); setIsDeleteModalOpen(true); }}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
        {!loadingData && filteredFolders.length === 0 && filteredFiles.length === 0 && (
          <div className="py-20 flex flex-col items-center justify-center text-slate-600 gap-4 opacity-50"><HiOutlineEmojiSad size={48} /><p className="font-bold text-sm uppercase tracking-widest">No items found</p></div>
        )}
      </section>

      <ViewFileModal file={viewingFile} isOpen={!!viewingFile} onClose={() => setViewingFile(null)} />
      <RenameModal isOpen={!!renamingItem} onClose={() => setRenamingItem(null)} currentName={renamingItem?.name} onRename={handleRenameSubmit} />
      <ShareModal isOpen={!!sharingItem} onClose={() => setSharingItem(null)} resource={sharingItem} onShare={handleShareSubmit} />
      <DownloadConfirmModal isOpen={!!downloadItem} onClose={() => setDownloadItem(null)} file={downloadItem} onConfirm={() => window.dispatchEvent(new CustomEvent('triggerDownload', { detail: { fileId: downloadItem.id } }))} />
      <DeleteModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={() => confirmItemDeletion(itemToDelete, itemToDelete?.type)} item={itemToDelete} isDeleting={isDeleting} />

      {/* Multi-Upload Status Panel */}
      <AnimatePresence>
        {activeUploads.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className={`fixed bottom-6 right-6 z-[200] bg-white border border-slate-200 shadow-2xl transition-all duration-500 overflow-hidden ${isUploadPanelMinimized ? "w-72 rounded-2xl" : "w-96 rounded-[28px]"}`}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 ${isUploadPanelMinimized ? "bg-slate-50/50" : "bg-white"}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${uploading ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                  <HiCloudUpload size={18} className={uploading ? "animate-bounce" : ""} />
                </div>
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                    {uploading ? "Uploading Assets" : "Uploads Complete"}
                  </h3>
                  {!isUploadPanelMinimized && (
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                      {activeUploads.filter(u => u.status === 'success').length} of {activeUploads.length} finished
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setIsUploadPanelMinimized(!isUploadPanelMinimized)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors">
                  {isUploadPanelMinimized ? <HiOutlineChevronUp size={16} /> : <HiOutlineChevronDown size={16} />}
                </button>
                <button onClick={() => setActiveUploads([])} className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-500 transition-colors">
                  <HiX size={16} />
                </button>
              </div>
            </div>

            {/* List */}
            <motion.div
              initial={false}
              animate={{ height: isUploadPanelMinimized ? 0 : "auto", opacity: isUploadPanelMinimized ? 0 : 1 }}
              className="overflow-hidden bg-slate-50/30"
            >
              <div className="max-h-[360px] overflow-y-auto no-scrollbar p-3 space-y-2">
                {activeUploads.map((upload) => (
                  <div key={upload.id} className="bg-white border border-slate-100 p-3 rounded-2xl shadow-sm group hover:border-indigo-100 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${upload.status === 'success' ? 'bg-emerald-50 text-emerald-600' :
                        upload.status === 'error' ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600'}`}>
                        {upload.status === 'success' ? <HiCheckCircle size={18} /> :
                          upload.status === 'error' ? <HiExclamationCircle size={18} /> :
                            upload.status === 'pending' ? <HiClock size={18} /> :
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center gap-2 mb-0.5">
                          <p className="text-[11px] font-bold text-slate-900 truncate uppercase tracking-tight">{upload.name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-slate-400">{upload.progress}%</span>
                            {(upload.status === 'uploading' || upload.status === 'pending') && (
                              <button onClick={() => handleCancelIndividualUpload(upload.id)} className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-500 transition-all border border-transparent hover:border-rose-100" title="Cancel specific upload"><HiX size={14} /></button>
                            )}
                          </div>
                        </div>
                        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${upload.progress}%` }} className={`h-full transition-colors duration-500 ${upload.status === 'success' ? 'bg-emerald-500' : upload.status === 'error' ? 'bg-rose-500' : 'bg-indigo-600'}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <CreateFolderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSuccess={() => fetchData()} parentId={currentFolderId} isPrivate={true} />
    </div>
  );
}
